import emploApiClient from './emploApiClient';

export interface UpsertContactRequest {
  Email: string;
  FirstName?: string;
  LastName?: string;
  PhoneNumber?: string;
  ExternalId?: string;
  AnonymousId?: string;
  AlternateEmails?: string[];
  CustomFields?: Record<string, string>;
  Address?: AddressInfo;
  ListIds?: string[];
}

export interface AddressInfo {
  AddressLine1?: string;
  AddressLine2?: string;
  City?: string;
  StateProvinceRegion?: string;
  PostalCode?: string;
  Country?: string;
}

export interface SendEmailRequest {
  toEmail: string;
  fromEmail?: string;
  fromName?: string;
  subject: string;
  htmlContent: string;
}

export interface MarketingListRequest {
  email: string;
  listId: string;
}

class SendGridService {
  /**
   * Add or update a single contact in SendGrid marketing lists
   */
  async upsertContact(request: UpsertContactRequest): Promise<boolean> {
    try {
      const response = await emploApiClient.post('/api/v1/email/marketing/upsert', request);
      return response.status === 200;
    } catch (error) {
      console.error('Error upserting contact:', error);
      throw error;
    }
  }

  /**
   * Add a single contact to a marketing list
   * This is a convenience method that uses upsertContact internally
   */
  async addContactToList(email: string, firstName: string, lastName: string, phoneNumber: string, customFields: Record<string, string>, listId: string): Promise<boolean> {
    return this.upsertContact({
      Email: email,
      FirstName: firstName,
      LastName: lastName,
      PhoneNumber: phoneNumber,
      CustomFields: customFields,
      ListIds: [listId]
    });
  }

  /**
   * Add contact from Facebook-style form with UTM parameters
   */
  async addFacebookFormContact(
    formData: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      homeschoolType: string;
      studentAge: string;
      homeschoolRole: string;
      Free_Class_Date?: string;
    },
    utmParams: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
    },
    listId: string
  ): Promise<boolean> {
    const customFields: Record<string, string> = {
      Homeschool_Type: formData.homeschoolType,
      School_Level: formData.studentAge,
      Role: formData.homeschoolRole,
      Source: 'facebook_landing_form_hs_summer25',
      Signup_Date: new Date().toISOString(),
      Agree_To_Privacy_Policy: '1', // Convert to string as DTO expects string values
      // Add Free Class Date if provided
      ...(formData.Free_Class_Date && { Free_Class_Date: formData.Free_Class_Date }),
      // Add UTM parameters for tracking
      UTM_Source: utmParams.utm_source || '',
      UTM_Medium: utmParams.utm_medium || '',
      UTM_Campaign: utmParams.utm_campaign || '',
      UTM_Term: utmParams.utm_term || '',
      UTM_Content: utmParams.utm_content || '',
      Landing_Page: window.location.pathname + window.location.search
    };

    return this.addContactToList(
      formData.email,
      formData.firstName,
      formData.lastName,
      formData.phone || '',
      customFields,
      listId
    );
  }

  /**
   * Add contact from date preference form with scheduling preferences
   */
  async addDatePreferenceContact(
    formData: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      homeschoolType: string;
      studentAge: string;
      homeschoolRole: string;
      preferredDays: string[];
      preferredTimes: string[];
      additionalComments: string;
    },
    utmParams: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
    },
    listId: string
  ): Promise<boolean> {
    const customFields: Record<string, string> = {
      Homeschool_Type: formData.homeschoolType,
      School_Level: formData.studentAge,
      Role: formData.homeschoolRole,
      Source: 'date_preference_form',
      Signup_Date: new Date().toISOString(),
      Agree_To_Privacy_Policy: '1', // Convert to string as DTO expects string values
      // Date preference specific fields
      Preferred_Days: formData.preferredDays.join(','),
      Preferred_Times: formData.preferredTimes.join(','),
      Additional_Comments: formData.additionalComments || '',
      // Add UTM parameters for tracking
      UTM_Source: utmParams.utm_source || '',
      UTM_Medium: utmParams.utm_medium || '',
      UTM_Campaign: utmParams.utm_campaign || '',
      UTM_Term: utmParams.utm_term || '',
      UTM_Content: utmParams.utm_content || '',
      Landing_Page: window.location.pathname + window.location.search
    };

    return this.addContactToList(
      formData.email,
      formData.firstName,
      formData.lastName,
      formData.phone || '',
      customFields,
      listId
    );
  }

  /**
   * Remove a contact from a marketing list
   */
  async removeContactFromList(email: string, listId: string): Promise<boolean> {
    try {
      const response = await emploApiClient.post('/api/email/marketing/remove', {
        email,
        listId
      });
      return response.status === 200;
    } catch (error) {
      console.error('Error removing contact from list:', error);
      throw error;
    }
  }

  /**
   * Get contact status by email
   */
  async getContactStatus(email: string): Promise<any> {
    try {
      const response = await emploApiClient.get(`/api/email/marketing/status/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      console.error('Error getting contact status:', error);
      throw error;
    }
  }

  /**
   * Send a general email
   */
  async sendEmail(request: SendEmailRequest): Promise<boolean> {
    try {
      const response = await emploApiClient.post('/api/email/send', request);
      return response.status === 200;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  }

  /**
   * Send a support email
   */
  async sendSupportEmail(request: SendEmailRequest): Promise<boolean> {
    try {
      const response = await emploApiClient.post('/api/email/support', request);
      return response.status === 200;
    } catch (error) {
      console.error('Error sending support email:', error);
      throw error;
    }
  }

  /**
   * Send an invitation email
   */
  async sendInvitation(email: string, inviteUrl: string): Promise<boolean> {
    try {
      const response = await emploApiClient.post('/api/email/invite', {
        email,
        inviteUrl
      });
      return response.status === 200;
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const sendGridService = new SendGridService();
export default sendGridService; 